import React from "react";
import { ChevronLeft } from "lucide-react";
import { useIsMobile } from "../hooks/useIsMobile";
import { useSwipeToOpen } from "../hooks/useSwipeToClose";
import Tooltip from "./ui/Tooltip";
import {
  INSPECTOR_CHROME_HOVER_SURFACE,
  INSPECTOR_CHROME_NO_FOCUS_RING,
} from "../utils/inspectorChromeButtonClasses";

const PANEL_MARGIN = 20;

/**
 * Third left-edge tab (mobile narrow): toggles the user-pinned tool panel, matching main menu +
 * inspector minimized tab affordances.
 */
export default function MobilePinnedToolPanelTab({
  pinnedId,
  isOpen,
  Icon,
  label,
  suppressed,
  onToggle,
  onSwipeOpen,
}) {
  const isMobile = useIsMobile();

  const swipeHandlers = useSwipeToOpen({
    enabled: Boolean(
      isMobile && pinnedId && !isOpen && !suppressed && typeof onSwipeOpen === "function"
    ),
    onOpen: onSwipeOpen,
  });

  if (!isMobile || !pinnedId || suppressed) {
    return null;
  }

  const labelText = isOpen ? `Hide ${label}` : `Show ${label}`;
  const tab = (
    <button
      type="button"
      onClick={onToggle}
      className={`mobile-pinned-tool-panel-tab fixed flex flex-row items-center justify-center gap-0.5 max-[720px]:justify-stretch max-[720px]:gap-0 rounded-l-md border border-r-0 border-gray-700 text-gray-300 hover:text-white transition-colors cursor-pointer ${INSPECTOR_CHROME_NO_FOCUS_RING} ${INSPECTOR_CHROME_HOVER_SURFACE}`}
      {...swipeHandlers}
      style={{
        top: PANEL_MARGIN,
        right: 0,
        width: "3rem",
        height: "2.5rem",
        backgroundColor: "var(--panel-surface)",
        zIndex: 200,
      }}
      aria-label={labelText}
    >
      <span className="flex items-center justify-center text-gray-200 max-[720px]:min-w-0 max-[720px]:flex-1 max-[720px]:basis-0 max-[720px]:pl-1.5">
        <span className="flex h-4 w-4 items-center justify-center">
          <Icon className="h-4 w-4 flex-shrink-0 text-white" aria-hidden />
        </span>
      </span>
      <span className="flex items-center justify-center max-[720px]:min-w-0 max-[720px]:flex-1 max-[720px]:basis-0 max-[720px]:px-2">
        <ChevronLeft
          className="mobile-pinned-tool-panel-tab-chevron h-3.5 w-3.5 max-[720px]:h-5 max-[720px]:w-5 text-white"
          strokeWidth={2.5}
          aria-hidden
        />
      </span>
    </button>
  );

  return (
    <Tooltip content={labelText} side="left">
      {tab}
    </Tooltip>
  );
}
